import { Link } from 'react-router-dom';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { accent } from '@/components/feature/solution/theme';

interface MatrixItem {
  icon: string;
  location: string;
  title: string;
  status?: string;
  lines: string[];
  href: string;
}

interface ParkingProductsProps {
  data: {
    eyebrow: string;
    title: string;
    subtitle: string;
    items: MatrixItem[];
    note: string;
  };
}

export default function ParkingProducts({ data }: ParkingProductsProps) {
  const a = accent('gold');

  return (
    <section id="products" className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100 scroll-mt-20">
      <div className="max-w-[1200px] mx-auto">
        <SectionHeading eyebrow={data.eyebrow} title={data.title} subtitle={data.subtitle} />

        <Reveal className="mt-10 md:mt-12" delay={80}>
          <div className="w-full overflow-hidden rounded-2xl border border-background-200 bg-background-50">
            <img
              src="https://static.readdy.ai/image/4482546e014370507f3d90ff31782979/e003860fe4ad1a78b7d6dfdbee6e1277.png"
              alt="智慧物流软硬件产品矩阵，涵盖智能视觉监控、智慧物联感知、商用显示、ePin 扫码溯源与九畴云市平台终端五条产品线共 28 类设备"
              title="智慧物流软硬件产品矩阵"
              className="w-full h-auto object-top"
            />
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.items.map((item, idx) => {
            const inner = (
              <div
                className={`h-full flex flex-col rounded-2xl border bg-background-50 p-6 transition-all duration-300 hover:-translate-y-1.5 ${
                  item.status ? 'border-dashed border-secondary-300 hover:border-secondary-400' : 'border-background-200 hover:border-secondary-300'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className={`w-12 h-12 flex items-center justify-center rounded-xl ${a.iconBox}`}>
                    <i className={`${item.icon} text-2xl`}></i>
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-background-100 border border-background-200 text-[11px] text-foreground-500 whitespace-nowrap">
                    {item.location}
                  </span>
                </div>

                <h3 className="mt-5 font-heading font-bold text-foreground-950 text-lg">{item.title}</h3>

                {item.status && (
                  <span className="mt-2 inline-flex w-fit items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 border border-dashed border-secondary-400 text-[11px] whitespace-nowrap">
                    <i className="ri-time-line"></i>
                    {item.status}
                  </span>
                )}

                <div className="mt-4 flex flex-col gap-2">
                  {item.lines.map((line) => (
                    <span key={line} className="flex items-start gap-2 text-sm text-foreground-600 leading-relaxed">
                      <i className="ri-checkbox-blank-circle-fill text-[6px] text-secondary-500 mt-2 shrink-0"></i>
                      {line}
                    </span>
                  ))}
                </div>

                <div className="mt-auto pt-5">
                  {item.href ? (
                    <span className="inline-flex items-center gap-1.5 text-sm text-secondary-700">
                      查看产品
                      <i className="ri-arrow-right-line transition-transform duration-300 group-hover/card:translate-x-1"></i>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-sm text-foreground-400">
                      <i className="ri-information-line"></i>
                      方案能力卡
                    </span>
                  )}
                </div>
              </div>
            );

            return (
              <Reveal key={item.title} delay={idx * 70} className="h-full">
                {item.href ? (
                  <Link to={item.href} className="group/card block h-full cursor-pointer">
                    {inner}
                  </Link>
                ) : (
                  inner
                )}
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-6" delay={100}>
          <div className="flex items-start gap-3 rounded-2xl border border-background-200 bg-background-50 px-5 py-4">
            <i className="ri-information-line text-secondary-600 text-lg mt-0.5"></i>
            <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{data.note}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}